import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  InputGroup,
  Form,
  Icon,
  Input,
} from "@clarkinc/zest-design-system/ui";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import SearchResult from "./SearchResult";
import SearchResultNotFound from "./SearchResultNotFound";
import SupportFAQ from "../Models/SupportFAQ";

type FormFields = {
  searchPhrase: string;
};

export interface FormProps {
  displayHeader: string;
  searchText: string;
}

const validationSchema = z.object({
  searchPhrase: z
    .string({ required_error: "A search phrase is required." })
    .nonempty("A search phrase is required."),
});

export type Props = FormProps;

export default function Search({ displayHeader, searchText }: Props) {
  const [showResults, setShowResults] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<SupportFAQ[]>();
  const searchPhraseRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchText && searchText.length > 0) {
      getSearchResults(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm<z.infer<typeof validationSchema>>({
    resolver: zodResolver(validationSchema),
  });

  const getSearchResults = async (data: string) => {
    setSearchValue(data);

    try {
      const result = await fetch("/api/searchsupportfaq/", {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (result.ok) {
        setShowResults(true);
        const supportFaqs = await result.json<SupportFAQ[]>();
        setSearchResults(supportFaqs);
        return;
      }

      form.setError("searchPhrase", {
        type: "custom",
        message:
          "There was an issue submitting your request. Please try again later.",
      });
    } catch {
      form.setError("searchPhrase", {
        type: "custom",
        message:
          "There was an issue submitting your request. Please try again later.",
      });
    }
  };

  const handleSearch = (data: FormFields) => {
    getSearchResults(data.searchPhrase.trim());
  };

  useEffect(() => {
    if (form.formState.errors) {
      searchPhraseRef.current?.focus();
    }
  }, [form.formState.errors]);

  return (
    <div className="support-section" data-module="supportPage">
      <div className="support-header-container">
        <div className="support-header-text">
          <h1 className="page-header xs:text-2xl skip-to-main-target">
            {displayHeader}
          </h1>
          <p>
            Need help finding information? Take a look at some of our Frequently
            Asked Questions and helpful links.
          </p>
        </div>
        <div className="support-search mt-5">
          <Form
            form={form}
            role="search"
            onSubmit={form.handleSubmit(handleSearch)}
            aria-label="FAQ Search Form"
          >
            <InputGroup
              errorMessages={form.formState.errors.searchPhrase?.message ?? ""}
              hasError={!!form.formState.errors}
              inputSize="large"
              theme="wss"
              // Note: The difference in FAQs spelling is intentional to prevent screen readers from pronouncing FAQs as a single word (fah-cue-es)
              groupLabel="Search Our FAQ's"
            >
              <InputGroup.Wrapper
                theme="wss"
                className="text-sm gap-1 mb-1"
                aria-hidden
              >
                <InputGroup.Label className="leading-4 xsl:leading-5">
                  Search Our FAQs
                </InputGroup.Label>
                <InputGroup.Helper className="leading-4 xsl:leading-5 font-normal text-sm">
                  (Example: Do you ship internationally?)
                </InputGroup.Helper>
              </InputGroup.Wrapper>

              <InputGroup.Wrapper>
                <Input
                  prefix={<Icon name="ri-search-line" theme="wss" />}
                  theme="wss"
                  label="Search Phrase"
                  helperText="Example: Do you ship internationally?"
                  name="searchPhrase"
                  value={form.watch("searchPhrase")}
                  onValueChange={(value) =>
                    form.setValue("searchPhrase", value)
                  }
                  ref={searchPhraseRef}
                  errorMessages={
                    form.formState.errors.searchPhrase?.message ?? ""
                  }
                  hasError={!!form.formState.errors?.searchPhrase}
                  required
                  hideLabel
                  hideError
                />

                <Button
                  theme="wss"
                  color="gray"
                  size="large"
                  type="submit"
                  className="relative"
                  onClick={form.handleSubmit(handleSearch)}
                >
                  Search
                </Button>
              </InputGroup.Wrapper>
              <InputGroup.Error className="text-xs" aria-hidden />
            </InputGroup>
          </Form>
        </div>
      </div>
      <div aria-live="polite" aria-atomic key={searchValue}>
        {showResults &&
          searchResults &&
          (searchResults.length > 0 ? (
            <span className="sr-only">{`${searchResults.length} results found for ${searchValue}`}</span>
          ) : (
            <span className="sr-only">{`0 results found for ${searchValue}`}</span>
          ))}
      </div>
      {showResults && searchResults && searchResults.length > 0 && (
        <SearchResult {...{ searchValue, searchResults }} />
      )}
      {showResults && searchResults && searchResults?.length === 0 && (
        <SearchResultNotFound {...{ searchValue }} />
      )}
    </div>
  );
}
